import { NotificationItem } from '@/hooks/common/useNotifications';

/**
 * Notification helper utilities for type checking and avatar props.
 * 
 * These utilities consolidate duplicated logic from:
 * - MobileNotificationCard
 * - DesktopNotificationsPopover
 * - NotificationRow
 */

/**
 * Check if a notification is a marketplace feedback request (buyer needs to leave feedback).
 */
export const isMarketplaceFeedback = (notification: NotificationItem): boolean => {
  return notification.notificationType === 'marketplace_feedback';
};

/**
 * Check if a notification is a marketplace notification (seller received feedback, etc.).
 */
export const isMarketplaceNotification = (notification: NotificationItem): boolean => {
  return notification.notificationType === 'marketplace_notification';
};

/**
 * Check if a notification is a property chat summary.
 */
export const isPropertyChat = (notification: NotificationItem): boolean => {
  return notification.notificationType === 'property_chat';
};

/**
 * Check if a notification is a regular bulletin.
 */
export const isBulletin = (notification: NotificationItem): boolean => {
  return notification.notificationType === 'bulletin';
};

/**
 * Check if a notification is any marketplace-related type.
 */
export const isMarketplaceRelated = (notification: NotificationItem): boolean => {
  return isMarketplaceFeedback(notification) || isMarketplaceNotification(notification);
};

/**
 * Avatar variant types for notification display.
 */
export type NotificationAvatarVariant = 'property' | 'marketplace' | 'message';

/**
 * Get the avatar variant for a notification based on its type.
 */
export const getNotificationVariant = (notification: NotificationItem): NotificationAvatarVariant => {
  if (isPropertyChat(notification)) {
    return 'message';
  }
  if (isMarketplaceRelated(notification)) {
    return 'marketplace';
  }
  return 'property';
};

/**
 * Get the listing image URL for marketplace notifications.
 * Returns undefined for non-marketplace notifications.
 */
export const getListingImageUrl = (notification: NotificationItem): string | undefined => {
  if (isMarketplaceFeedback(notification)) {
    return notification.feedbackRequest?.listing?.image;
  }
  if (isMarketplaceNotification(notification)) {
    return notification.marketplaceNotification?.metadata?.listingImageUrl ?? undefined;
  }
  return undefined;
};

/**
 * Props for the NotificationAvatar component.
 */
export interface NotificationAvatarProps {
  variant: NotificationAvatarVariant;
  imageUrl: string | undefined;
}

/**
 * Get props for the NotificationAvatar component based on notification type.
 * 
 * @example
 * ```tsx
 * const avatarProps = getNotificationAvatarProps(notification);
 * <NotificationAvatar {...avatarProps} showUnreadDot={isUnread} />
 * ```
 */
export const getNotificationAvatarProps = (notification: NotificationItem): NotificationAvatarProps => ({
  variant: getNotificationVariant(notification),
  imageUrl: getListingImageUrl(notification),
});

/**
 * Get received feedback metadata from a marketplace notification.
 * Returns undefined if not a marketplace notification or no metadata.
 */
export const getReceivedFeedbackData = (notification: NotificationItem) => {
  if (!isMarketplaceNotification(notification)) {
    return undefined;
  }
  
  const metadata = notification.marketplaceNotification?.metadata;
  if (!metadata) {
    return undefined;
  }

  return {
    listingImageUrl: metadata.listingImageUrl,
    buyerName: metadata.buyerName || 'Unknown',
    rating: metadata.rating || 0,
    reviewText: metadata.reviewText || '',
    listingTitle: metadata.listingTitle || notification.description || '',
  };
};
